import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Calendar, User, ArrowLeft, BookOpen, ArrowRight } from "lucide-react";
import { PolicyPageShell, PolicyHeader } from "@/components/PolicyHeader";
import { Blogs as BlogsApi } from "@/lib/api";

export default function Blogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    BlogsApi.list()
      .then((data) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PolicyPageShell
      testId="blogs-page"
      eyebrow="Sahyog Insights"
      title={
        <>
          Money <em className="not-italic text-[#D4AF37]">Stories</em>,{" "}
          <span className="font-script italic text-[#F0D788]">Smart Guides</span>
        </>
      }
      lead="Curated reading on credit, loans, DSA growth and financial planning — written by our advisor desk and founders."
    >
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={32} className="text-[#F0D788] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <EmptyBlogs />
      ) : (
        <div className="grid md:grid-cols-2 gap-7">
          {items.map((b) => (
            <BlogCard key={b.id} blog={b} />
          ))}
        </div>
      )}
    </PolicyPageShell>
  );
}

function BlogCard({ blog }) {
  return (
    <Link
      to={`/blogs/${blog.slug}`}
      data-testid={`blog-card-${blog.slug}`}
      className="group relative rounded-3xl p-7 border border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all flex flex-col"
    >
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
        {blog.category}
      </div>
      <h2 className="mt-3 font-display text-xl md:text-2xl text-white group-hover:text-[#F0D788] transition-colors leading-snug">
        {blog.title}
      </h2>
      <p className="mt-3 text-white/65 text-sm leading-relaxed flex-1 line-clamp-3">
        {blog.excerpt}
      </p>
      <div className="mt-6 flex items-center justify-between text-[11px] text-white/45">
        <span className="flex items-center gap-1.5">
          <User size={11} /> {blog.author}
        </span>
        <span className="flex items-center gap-1.5">
          <Calendar size={11} />{" "}
          {new Date(blog.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
        </span>
      </div>
      <div className="mt-5 inline-flex items-center gap-2 text-[#F0D788] text-xs uppercase tracking-[0.2em]">
        Read article <ArrowRight size={13} />
      </div>
    </Link>
  );
}

function EmptyBlogs() {
  return (
    <div className="text-center py-20">
      <BookOpen size={32} className="mx-auto text-[#F0D788]/55" />
      <h3 className="mt-4 font-display text-2xl text-white">No posts yet</h3>
      <p className="mt-2 text-white/55 text-sm">Our writers are sharpening their pencils. Check back soon.</p>
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    BlogsApi.get(slug)
      .then((data) => setPost(data))
      .catch((err) =>
        setError(err?.response?.status === 404 ? "Post not found." : "Could not load post.")
      )
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div data-testid="blog-detail-page" className="min-h-screen bg-[#04102B] text-white">
      <PolicyHeader testId="blog-detail-header" />
      <main className="max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-20">
        <Link
          to="/blogs"
          data-testid="blog-back-list"
          className="inline-flex items-center gap-1.5 text-white/55 hover:text-[#F0D788] text-xs uppercase tracking-[0.2em]"
        >
          <ArrowLeft size={13} /> All Posts
        </Link>
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="text-[#F0D788] animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-20 text-white/65" data-testid="blog-detail-error">
            {error}
          </div>
        ) : post ? (
          <article className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
              {post.category}
            </div>
            <h1 className="mt-3 font-display text-3xl md:text-5xl leading-tight tracking-tight">
              {post.title}
            </h1>
            <div className="mt-5 flex items-center gap-4 text-white/50 text-xs">
              <span className="flex items-center gap-1.5">
                <User size={12} /> {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar size={12} />{" "}
                {new Date(post.created_at).toLocaleDateString("en-IN", { dateStyle: "long" })}
              </span>
            </div>
            <p className="mt-8 text-white/80 text-lg leading-relaxed italic border-l-2 border-[#D4AF37] pl-5">
              {post.excerpt}
            </p>
            <div className="mt-10 text-white/85 leading-[1.85] whitespace-pre-wrap">
              {post.body}
            </div>
            <div className="mt-12 rounded-3xl p-7 border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent">
              <h3 className="font-display text-xl">Ready to take action?</h3>
              <p className="mt-2 text-white/65 text-sm">
                Talk to a Sahyog advisor — free consultation, no obligation.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/apply/loan" className="px-5 py-2.5 rounded-full btn-gold text-sm font-semibold">
                  Apply for a Loan
                </Link>
                <a
                  href="https://wa.me/917016975324"
                  target="_blank"
                  rel="noreferrer"
                  className="px-5 py-2.5 rounded-full border border-emerald-500/30 bg-emerald-600/15 text-emerald-200 text-sm font-semibold"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>
          </article>
        ) : null}
      </main>
    </div>
  );
}
